/**
 * Test Organization Filtering Hook
 * 
 * Provides utilities for filtering out test organization data from queries.
 * Test organizations are identified by the is_test flag on the organizations table.
 * 
 * @example
 * const { testOrgIds, testUserIds, isLoading } = useTestOrganizations();
 * 
 * // Filter out test org data in queries
 * if (testOrgIds.length > 0 && !includeTestData) {
 *   query = query.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
 * }
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TestOrganizationData {
  testOrgIds: string[];
  testUserIds: string[];
}

/**
 * Hook to fetch and cache test organization IDs and their user IDs.
 * Results are cached for 10 minutes since test orgs rarely change.
 */
export function useTestOrganizations() {
  return useQuery({
    queryKey: ['test-organizations'],
    queryFn: async (): Promise<TestOrganizationData> => {
      // Get test organization IDs
      const { data: testOrgs, error: orgsError } = await supabase
        .from('organizations')
        .select('id')
        .eq('is_test', true);

      if (orgsError) {
        console.error('Error fetching test organizations:', orgsError);
        throw orgsError;
      }

      const testOrgIds = testOrgs?.map(o => o.id) || [];

      // Get user IDs from test organizations
      let testUserIds: string[] = [];
      if (testOrgIds.length > 0) {
        const { data: testProfiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id')
          .in('organization_id', testOrgIds);

        if (profilesError) {
          console.error('Error fetching test profiles:', profilesError);
          throw profilesError;
        }

        testUserIds = testProfiles?.map(p => p.user_id) || [];
      }

      return { testOrgIds, testUserIds };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - test orgs rarely change
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
}

/**
 * Standalone function to fetch test organization IDs.
 * Useful for hooks that don't want the React Query wrapper.
 */
export async function fetchTestOrganizationIds(): Promise<string[]> {
  const { data: testOrgs, error } = await supabase
    .from('organizations')
    .select('id')
    .eq('is_test', true);

  if (error) {
    console.error('Error fetching test organizations:', error);
    return [];
  }

  return testOrgs?.map(o => o.id) || [];
}

/**
 * Standalone function to fetch user IDs from test organizations.
 */
export async function fetchTestUserIds(testOrgIds?: string[]): Promise<string[]> {
  const orgIds = testOrgIds ?? await fetchTestOrganizationIds();

  if (orgIds.length === 0) return [];

  const { data: testProfiles, error } = await supabase
    .from('profiles')
    .select('user_id')
    .in('organization_id', orgIds);

  if (error) {
    console.error('Error fetching test profiles:', error);
    return [];
  }

  return testProfiles?.map(p => p.user_id) || [];
}

/**
 * Get job IDs from test organizations
 */
export async function fetchTestJobIds(testOrgIds?: string[]): Promise<string[]> {
  const orgIds = testOrgIds ?? await fetchTestOrganizationIds();

  if (orgIds.length === 0) return [];

  const { data: testJobs, error } = await supabase
    .from('jobs')
    .select('id')
    .in('organization_id', orgIds);

  if (error) {
    console.error('Error fetching test jobs:', error);
    return [];
  }

  return testJobs?.map(j => j.id) || [];
}
